/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import Lightbox, { IconButton } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/dist/plugins/zoom';
import 'yet-another-react-lightbox/dist/styles.css';

import PureButton from 'ui/components/pure-controls/PureButton';
import { API } from 'API';

import './IdViewer.scss';

const idSides = ['front', 'back'];
const LIGHTBOX_TOOLBAR_INSET = 16;
const LIGHTBOX_TOOLBAR_GAP = 8;
const LIGHTBOX_PILL_GAP = 12;

// `kbaDateOfBirth` is expected to come back as an ISO date string
// (YYYY-MM-DD). Parse the parts directly rather than `new Date(...)` to
// avoid timezone shifting the displayed day. If the value doesn't match
// that exact shape (e.g. a full ISO timestamp, or anything unexpected),
// fail safely by returning null rather than rendering `undefined/undefined/...`.
const KBA_DATE_OF_BIRTH_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatKbaDateOfBirth = isoDate => {
  if (!isoDate) {
    return null;
  }

  const match = KBA_DATE_OF_BIRTH_PATTERN.exec(isoDate);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

// Lays out the controls pill and the close pill as a single row sitting directly
// above the image. Both are returned as viewport coordinates of their left/top
// edges. When the image is too tall, zoomed, rotated, or panned such that its top
// edge is off-screen, the row is pinned just inside the top of the viewport so the
// controls stay reachable.
export const getLightboxControlsLayout = ({ imageRect, toolbarRect, closeRect, viewportWidth }) => {
  if (!imageRect || !toolbarRect || !closeRect || !viewportWidth) {
    return null;
  }

  const rowHeight = Math.max(toolbarRect.height, closeRect.height);
  const top = Math.max(LIGHTBOX_TOOLBAR_INSET, imageRect.top - rowHeight - LIGHTBOX_TOOLBAR_GAP);

  // The close pill hangs off the right edge of the image, but never past the viewport.
  const closeLeft = clamp(
    imageRect.left + imageRect.width - closeRect.width,
    LIGHTBOX_TOOLBAR_INSET,
    Math.max(LIGHTBOX_TOOLBAR_INSET, viewportWidth - LIGHTBOX_TOOLBAR_INSET - closeRect.width),
  );

  // The controls pill is centered on the image, then pushed left as needed so it
  // never runs into the close pill on a narrow image.
  const centeredLeft = imageRect.left + imageRect.width / 2 - toolbarRect.width / 2;
  const rightmostLeft = closeLeft - LIGHTBOX_PILL_GAP - toolbarRect.width;
  const toolbarLeft = Math.max(
    LIGHTBOX_TOOLBAR_INSET,
    Math.min(
      clamp(
        centeredLeft,
        LIGHTBOX_TOOLBAR_INSET,
        Math.max(
          LIGHTBOX_TOOLBAR_INSET,
          viewportWidth - LIGHTBOX_TOOLBAR_INSET - toolbarRect.width,
        ),
      ),
      rightmostLeft,
    ),
  );

  return {
    toolbar: { top, left: toolbarLeft },
    close: { top, left: closeLeft },
  };
};

const RotateLeftIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7.1 8.5 5.7 7.1 1 11.8l4.7 4.7 1.4-1.4-2.3-2.3H11a5 5 0 1 1-5 5H4a7 7 0 1 0 7-7H4.8l2.3-2.3Z" />
  </svg>
);

const RotateRightIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden="true">
    <path d="m18.3 7.1-1.4 1.4 2.3 2.3H13a7 7 0 1 0 7 7h-2a5 5 0 1 1-5-5h6.2l-2.3 2.3 1.4 1.4 4.7-4.7-4.7-4.7Z" />
  </svg>
);

const CloseIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4Z" />
  </svg>
);

export class IdViewer extends React.Component {
  static propTypes = {
    session: PropTypes.object.isRequired,
    testatorString: PropTypes.string.isRequired,
    approveId: PropTypes.func.isRequired,
    rejectId: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
  };

  constructor(...args) {
    super(...args);
    this.state = {
      lightboxOpen: false,
      lightboxIndex: 0,
      rotation: 0,
      idUrls: null,
      idDimensions: {},
      error: false,
    };
    this.lightboxToolbarAnimationFrame = null;
  }

  componentDidMount() {
    this.fetchImageUrls();
  }

  componentDidUpdate(_, previousState) {
    const { lightboxOpen } = this.state;

    if (lightboxOpen && !previousState.lightboxOpen) {
      this.startLightboxToolbarPositioning();
    } else if (!lightboxOpen && previousState.lightboxOpen) {
      this.stopLightboxToolbarPositioning();
    }
  }

  componentWillUnmount() {
    this.stopLightboxToolbarPositioning();
  }

  fetchImageUrls() {
    const { session } = this.props;

    if (!session || !session.id) {
      this.setState({ error: true });
    } else {
      API.getNotarizationAdminIdImageUrls(session.id)
        .then(result => {
          this.setState({ idUrls: result.data });
        })
        .catch(() => this.setState({ error: true }));
    }
  }

  tryAgain() {
    this.setState({ error: false });
    this.fetchImageUrls();
  }

  get slides() {
    const { testatorString } = this.props;
    const { idUrls, idDimensions } = this.state;

    if (!idUrls || !idUrls[testatorString]) {
      return [];
    }

    return idSides
      .filter(side => Boolean(idUrls[testatorString][side]))
      .map(side => {
        const dimensions = idDimensions[`${testatorString}-${side}`];

        return {
          src: idUrls[testatorString][side],
          alt: `Customer ID ${side}`,
          ...(dimensions || {}),
        };
      });
  }

  handleIdImageLoad = (testatorString, side, img) => {
    if (!img) {
      return;
    }

    const { naturalWidth: width, naturalHeight: height } = img;
    const key = `${testatorString}-${side}`;

    if (width && height && !this.state.idDimensions[key]) {
      this.setState(state => ({
        idDimensions: {
          ...state.idDimensions,
          [key]: { width, height },
        },
      }));
    }
  };

  idImageRef = (testatorString, side) => img => {
    // Images already served from the browser cache can fire `load` before React
    // attaches the listener, so also check `complete` when the <img> ref mounts.
    if (img && img.complete) {
      this.handleIdImageLoad(testatorString, side, img);
    }
  };

  getLightboxToolbarButtons() {
    return [
      <IconButton
        key="rotate-left"
        label="Rotate left"
        icon={RotateLeftIcon}
        onClick={() => this.setState(state => ({ rotation: state.rotation - 90 }))}
      />,
      <IconButton
        key="rotate-right"
        label="Rotate right"
        icon={RotateRightIcon}
        onClick={() => this.setState(state => ({ rotation: state.rotation + 90 }))}
      />,
      'zoom',
    ];
  }

  openLightboxAtSide(side) {
    const slideIndex = this.slides.findIndex(
      slide => slide.src === this.state.idUrls[this.props.testatorString][side],
    );
    if (slideIndex < 0) {
      return;
    }

    this.setState({
      lightboxOpen: true,
      lightboxIndex: slideIndex,
      rotation: 0,
    });
  }

  closeLightbox = () => {
    this.setState({
      lightboxOpen: false,
      rotation: 0,
    });
  };

  handleLightboxView = ({ index }) => {
    this.setState({
      lightboxIndex: index,
      rotation: 0,
    });
  };

  positionLightboxToolbar = () => {
    const lightbox = document.querySelector('.id-viewer-lightbox');
    const image = lightbox && lightbox.querySelector('.yarl__slide_current .yarl__slide_image');
    const toolbar = lightbox && lightbox.querySelector('.yarl__toolbar');
    const close = lightbox && lightbox.querySelector('.id-viewer-lightbox__close');

    if (image && toolbar && close) {
      const layout = getLightboxControlsLayout({
        imageRect: image.getBoundingClientRect(),
        toolbarRect: toolbar.getBoundingClientRect(),
        closeRect: close.getBoundingClientRect(),
        viewportWidth: window.innerWidth,
      });

      if (layout) {
        toolbar.style.setProperty('--id-viewer-lightbox-toolbar-top', `${layout.toolbar.top}px`);
        toolbar.style.setProperty('--id-viewer-lightbox-toolbar-left', `${layout.toolbar.left}px`);
        close.style.setProperty('--id-viewer-lightbox-close-top', `${layout.close.top}px`);
        close.style.setProperty('--id-viewer-lightbox-close-left', `${layout.close.left}px`);
      }
    }

    this.lightboxToolbarAnimationFrame = window.requestAnimationFrame(this.positionLightboxToolbar);
  };

  startLightboxToolbarPositioning() {
    this.stopLightboxToolbarPositioning();
    this.lightboxToolbarAnimationFrame = window.requestAnimationFrame(this.positionLightboxToolbar);
  }

  stopLightboxToolbarPositioning() {
    if (this.lightboxToolbarAnimationFrame !== null) {
      window.cancelAnimationFrame(this.lightboxToolbarAnimationFrame);
      this.lightboxToolbarAnimationFrame = null;
    }
  }

  renderCloseControl = () => (
    <button
      type="button"
      className="id-viewer-lightbox__close"
      aria-label="Close"
      // The controls slot renders inside the lightbox's swipe sensor, so a small
      // drag while pressing Close would otherwise register as a slide swipe.
      onPointerDown={event => event.stopPropagation()}
      onClick={this.closeLightbox}
    >
      <span className="id-viewer-lightbox__close-label">Close</span>
      <CloseIcon className="id-viewer-lightbox__close-icon" />
    </button>
  );

  renderLightbox() {
    const { lightboxOpen, lightboxIndex, rotation } = this.state;
    const slides = this.slides;
    const activeSlide = slides[lightboxIndex];

    if (!lightboxOpen || !slides.length) {
      return null;
    }

    return (
      <Lightbox
        className="id-viewer-lightbox"
        open={lightboxOpen}
        close={this.closeLightbox}
        index={lightboxIndex}
        slides={slides}
        plugins={[Zoom]}
        zoom={{ maxZoomPixelRatio: 3 }}
        on={{ view: this.handleLightboxView }}
        toolbar={{ buttons: this.getLightboxToolbarButtons() }}
        render={{
          controls: this.renderCloseControl,
          slideContainer: ({ slide, children }) => (
            <div
              className="id-viewer-lightbox__slide-container"
              style={{
                transform: `rotate(${
                  activeSlide && activeSlide.src === slide.src ? rotation : 0
                }deg)`,
              }}
            >
              {children}
            </div>
          ),
        }}
      />
    );
  }

  renderKbaInfo() {
    const { testatorString } = this.props;
    const { idUrls } = this.state;
    const { kbaFirstName, kbaLastName, kbaDateOfBirth } = idUrls[testatorString] || {};

    const fullName = [kbaFirstName, kbaLastName].filter(Boolean).join(' ');
    const formattedDob = formatKbaDateOfBirth(kbaDateOfBirth);

    if (!fullName && !formattedDob) {
      return null;
    }

    return (
      <div className="id-viewer__kba-info">
        {fullName && (
          <p>
            Full Name: <strong>{fullName}</strong>
          </p>
        )}
        {formattedDob && (
          <p>
            Date of Birth: <strong>{formattedDob}</strong>
          </p>
        )}
      </div>
    );
  }

  render() {
    const { session, testatorString, approveId, rejectId, loading } = this.props;
    const { idUrls, error } = this.state;

    if (error) {
      return (
        <div className="id-viewer">
          <h4>There was an error loading the ID</h4>
          <PureButton onClick={this.tryAgain.bind(this)} className="button--primary">
            Try again
          </PureButton>
        </div>
      );
    }

    if (!session || !idUrls) {
      return null;
    }

    return (
      <div className="id-viewer">
        {this.renderLightbox()}
        <h4>Customer ID:</h4>
        {this.renderKbaInfo()}
        <div className="id-viewer__ids">
          {idSides.map(side => {
            if (idUrls[testatorString][side]) {
              return (
                <img
                  alt="Customer ID"
                  key={side}
                  src={idUrls[testatorString][side]}
                  ref={this.idImageRef(testatorString, side)}
                  onLoad={event =>
                    this.handleIdImageLoad(testatorString, side, event.currentTarget)
                  }
                  onClick={() => this.openLightboxAtSide(side)}
                />
              );
            }
          })}
        </div>
        <div className="id-viewer__actions">
          <PureButton onClick={approveId} loading={loading} className="button--primary">
            Approve
          </PureButton>
          <PureButton
            onClick={rejectId}
            loading={loading}
            className="button--tertiary button--error"
          >
            Decline
          </PureButton>
        </div>
      </div>
    );
  }
}
